// Универсальный hook для работы с localStorage.
//
// Особенности:
//   - namespace через префикс ключа (контракт: всё хранится под "multitool:*").
//   - Cross-tab синхронизация через storage event (window.addEventListener('storage')).
//   - JSON-сериализация значений. При невалидном JSON — фолбэк на defaultValue.
//   - Защита от QuotaExceededError: попытка set'а оборачивается в try/catch
//     и при провале возвращает false (UI может среагировать на это).
//
// LOGOUT cleanup: см. clearMultitoolKeys() в этом же файле — вызывается при logout.
import { useCallback, useEffect, useState } from "react";
import { LS_NAMESPACE } from "@/api/constants";

export { LS_NAMESPACE };

/** Setter поддерживает как прямое значение, так и functional update
 *  (по аналогии с React useState). Functional форма читает АКТУАЛЬНОЕ значение
 *  из localStorage, защищая от cross-tab race condition: если другая вкладка
 *  обновила storage между нашим прошлым read и текущим update, мы не затрём
 *  её изменения. */
type SetValueAction<T> = T | ((prev: T) => T);

/** Custom event для INTRA-tab синхронизации. Storage event браузера
 *  fire'ится только в ДРУГИХ вкладках — в той же вкладке экземпляры
 *  useLocalStorage для одного key не узнают об изменениях друг от друга
 *  (типичный сценарий: ThemeToggle в Header меняет тему, но useTheme в App
 *  читает старое значение → ConfigProvider не обновляется). Этот event
 *  закрывает дыру. */
const INTRA_TAB_EVENT = "multitool:ls:change";

interface IntraTabPayload {
  key: string;
  value: unknown;
}

/**
 * Hook возвращает [value, setValue, removeValue]. value реактивно обновляется
 * при изменениях в других вкладках (storage event).
 */
export function useLocalStorage<T>(
  shortKey: string,
  defaultValue: T,
): [T, (next: SetValueAction<T>) => boolean, () => void] {
  const key = `${LS_NAMESPACE}${shortKey}`;

  const readFromStorage = useCallback((): T => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  }, [key, defaultValue]);

  const [value, setValue] = useState<T>(readFromStorage);

  const setStored = useCallback(
    (next: SetValueAction<T>): boolean => {
      // Functional form: читаем АКТУАЛЬНОЕ значение из localStorage перед
      // применением. Это защита от race condition между вкладками — если
      // другая вкладка успела обновить storage, мы это увидим.
      const resolved =
        typeof next === "function"
          ? (next as (prev: T) => T)(readFromStorage())
          : next;
      try {
        if (typeof window === "undefined") return false;
        window.localStorage.setItem(key, JSON.stringify(resolved));
        setValue(resolved);
        // Уведомляем другие экземпляры useLocalStorage в ЭТОЙ ЖЕ вкладке.
        // Storage event браузера тут не сработает (только cross-tab).
        window.dispatchEvent(
          new CustomEvent<IntraTabPayload>(INTRA_TAB_EVENT, {
            detail: { key, value: resolved },
          }),
        );
        return true;
      } catch {
        // QuotaExceededError, SecurityError (private mode), etc.
        // В UI пока не падаем — state в памяти всё равно обновляем.
        setValue(resolved);
        return false;
      }
    },
    [key, readFromStorage],
  );

  const removeStored = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setValue(defaultValue);
  }, [key, defaultValue]);

  // Cross-tab sync (storage event — только из ДРУГИХ вкладок).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      if (event.newValue === null) {
        setValue(defaultValue);
        return;
      }
      try {
        setValue(JSON.parse(event.newValue) as T);
      } catch {
        // Невалидный JSON в чужой вкладке — игнорируем.
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, defaultValue]);

  // Intra-tab sync (custom event от других экземпляров useLocalStorage в ТОЙ ЖЕ
  // вкладке). Нужен потому что browser storage event не fire'ится in-tab.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onIntraTab = (event: Event) => {
      const detail = (event as CustomEvent<IntraTabPayload>).detail;
      if (!detail || detail.key !== key) return;
      setValue(detail.value as T);
    };
    window.addEventListener(INTRA_TAB_EVENT, onIntraTab);
    return () => window.removeEventListener(INTRA_TAB_EVENT, onIntraTab);
  }, [key]);

  return [value, setStored, removeStored];
}

/**
 * Удаляет ВСЕ ключи под namespace "multitool:*". Вызывается на logout —
 * чтобы на shared-компьютере следующий юзер не увидел чужие данные форм/настроек.
 */
export function clearMultitoolKeys(): void {
  if (typeof window === "undefined") return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(LS_NAMESPACE)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
